// Klaviyo client-side subscriptions API.
// Both KLAVIYO_COMPANY_ID and WAITLIST_LIST_ID are public values per
// Klaviyo's design — the client API is meant to be called from the
// browser. They're env-driven so they can be swapped per environment
// (dev/prod) without code changes.
const KLAVIYO_COMPANY_ID = process.env.REACT_APP_KLAVIYO_COMPANY_ID;
const WAITLIST_LIST_ID = process.env.REACT_APP_KLAVIYO_WAITLIST_LIST_ID;

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
const KLAVIYO_API_REVISION = '2024-10-15';

/**
 * Subscribe a profile to the Waitlist list in Klaviyo.
 *
 * Resolves to true on accepted (HTTP 2xx, typically 202). Throws on
 * misconfiguration (missing env vars) and on network/HTTP errors so
 * the caller can surface them. Caller is responsible for
 * toggling success/error UI.
 */
export const submitToWaitlist = async ({ email, firstName, zip, interests = [], source }) => {
  if (!KLAVIYO_COMPANY_ID || !WAITLIST_LIST_ID) {
    throw new Error(
      'Klaviyo not configured: REACT_APP_KLAVIYO_COMPANY_ID and REACT_APP_KLAVIYO_WAITLIST_LIST_ID must be set.'
    );
  }

  const url = `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(KLAVIYO_COMPANY_ID)}`;

  const profileAttributes = {
    email,
    properties: {
      waitlist_zip: zip || '',
      interests: interests.join(', '),
      source: source || 'waitlist_form',
    },
  };
  if (firstName) profileAttributes.first_name = firstName;
  if (zip) profileAttributes.location = { zip };

  const body = {
    data: {
      type: 'subscription',
      attributes: {
        profile: { data: { type: 'profile', attributes: profileAttributes } },
        custom_source: source || 'Waitlist Form',
      },
      relationships: {
        list: { data: { type: 'list', id: WAITLIST_LIST_ID } },
      },
    },
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      revision: KLAVIYO_API_REVISION,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok && response.status !== 202) {
    let detail = '';
    try {
      detail = await response.text();
    } catch (e) {
      // ignore
    }
    throw new Error(`Klaviyo subscription failed: ${response.status} ${detail.slice(0, 200)}`);
  }

  return true;
};
